// Ngân sách marketing đã duyệt theo tháng — quản lý nhập tay.
// Tạm lưu localStorage (FE trước); khi BE có API thì thay read/write bằng
// GET/PUT /api/v1/be/dashboard/v2/marketing-budgets (xem tài liệu Dac_ta_API_Dashboard_kinh_doanh.docx).
import { useEffect, useMemo, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';

export interface MonthBudget {
  /** Tổng ngân sách đã duyệt của tháng (VNĐ) */
  total: number;
  /** Phân bổ theo mạng quảng cáo — không bắt buộc */
  networks: Record<string, number>;
  note?: string;
  updatedAt: string;
}
/** Khóa tháng dạng YYYY-MM */
export type BudgetStore = Record<string, MonthBudget>;

export const BUDGET_KEY = 'dashboard-kinh-doanh.approved-budgets';
export const BUDGET_NETWORKS = ['Facebook', 'TikTok', 'Google', 'Zalo'];
const EVENT = 'approved-budget-change';

export function readBudgets(): BudgetStore {
  try {
    const v = JSON.parse(localStorage.getItem(BUDGET_KEY) || '{}');
    return v && typeof v === 'object' ? v : {};
  } catch { return {}; }
}

/** Lưu (hoặc xóa khi b = null) ngân sách một tháng */
export function saveMonthBudget(month: string, b: Omit<MonthBudget, 'updatedAt'> | null) {
  const store = readBudgets();
  if (b) store[month] = { ...b, updatedAt: dayjs().toISOString() };
  else delete store[month];
  try { localStorage.setItem(BUDGET_KEY, JSON.stringify(store)); } catch { /* chế độ riêng tư / hết dung lượng */ }
  window.dispatchEvent(new Event(EVENT));
}

export interface RangeBudget {
  total: number;
  networks: Record<string, number>;
  /** Mọi tháng trong kỳ đều đã nhập ngân sách */
  complete: boolean;
}

/**
 * Ngân sách đã duyệt cho một khoảng ngày: mỗi tháng chia đều theo ngày,
 * vd. xem 01/10 – 15/10 thì lấy 15/31 ngân sách tháng 10.
 */
export function budgetForRange(start: Dayjs, end: Dayjs, store: BudgetStore): RangeBudget | null {
  let total = 0;
  let found = false;
  let complete = true;
  const networks: Record<string, number> = {};
  for (let m = start.startOf('month'); !m.isAfter(end, 'month'); m = m.add(1, 'month')) {
    const b = store[m.format('YYYY-MM')];
    if (!b) { complete = false; continue; }
    found = true;
    const from = start.isAfter(m) ? start : m;
    const to = end.isBefore(m.endOf('month')) ? end : m.endOf('month');
    const share = (to.startOf('day').diff(from.startOf('day'), 'day') + 1) / m.daysInMonth();
    total += b.total * share;
    Object.entries(b.networks || {}).forEach(([k, v]) => { networks[k] = (networks[k] || 0) + v * share; });
  }
  return found ? { total: Math.round(total), networks, complete } : null;
}

/** Ngân sách đã duyệt cho kỳ đang xem và kỳ so sánh; tự cập nhật khi lưu ở tab / màn khác */
export function useApprovedBudget(start: Dayjs, end: Dayjs, prevStart: Dayjs, prevEnd: Dayjs) {
  const [store, setStore] = useState(readBudgets);
  useEffect(() => {
    const sync = () => setStore(readBudgets());
    const onStorage = (e: StorageEvent) => { if (e.key === BUDGET_KEY) sync(); };
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', onStorage);
    return () => { window.removeEventListener(EVENT, sync); window.removeEventListener('storage', onStorage); };
  }, []);
  const key = [start, end, prevStart, prevEnd].map(d => d.format('YYYY-MM-DD')).join('|');
  return useMemo(() => {
    const [s, e, ps, pe] = key.split('|').map(d => dayjs(d));
    return { cur: budgetForRange(s, e, store), prev: budgetForRange(ps, pe, store), store };
  }, [key, store]);
}
